import { useEffect, useState } from "react";
import { AsyncSelectOption } from "@/components/AsyncSelect";
import Flex from "@/components/Flex";
import ProviderSelect from "@/components/ProviderSelect";
import StyledDialog from "@/components/StyledDialog";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import { UseProvidersListProps } from "@/queries/provider/setting/use-providers-list";
import { DEF_PAGINATION } from "@/utils/utils";
import {
  PROVIDER_ASSIGNMENT_LABEL,
  PROVIDER_ASSIGNMENT_MESSAGE,
} from "../group-visit-constant";
import { ProviderAssignmentValues } from "../group-visit-types";

export type ProviderAssignmentModalProps = {
  open: boolean;
  onClose: () => void;
  /** Currently assigned providers. */
  values: ProviderAssignmentValues;
  onSubmit: (values: ProviderAssignmentValues) => Promise<unknown> | void;
  isSubmitting?: boolean;
};

/** Only active providers of the practice are listed. */
const PROVIDER_SEARCH_PARAM: UseProvidersListProps = {
  pagination: DEF_PAGINATION,
  status: true,
};

/**
 * Assigns a group's primary therapist and optional co-therapist.
 *
 * @author Rajkumar Rathod
 */
const ProviderAssignmentModal = ({
  open,
  onClose,
  values,
  onSubmit,
  isSubmitting,
}: ProviderAssignmentModalProps) => {
  const [primaryTherapist, setPrimaryTherapist] =
    useState<AsyncSelectOption | null>(values.primaryTherapist);
  const [coTherapist, setCoTherapist] = useState<AsyncSelectOption | null>(
    values.coTherapist
  );

  useEffect(() => {
    if (!open) return;
    setPrimaryTherapist(values.primaryTherapist);
    setCoTherapist(values.coTherapist);
  }, [open, values]);

  const isSamePerson =
    !!primaryTherapist?.value && primaryTherapist.value === coTherapist?.value;

  const error = !primaryTherapist
    ? PROVIDER_ASSIGNMENT_MESSAGE.primaryRequired
    : isSamePerson
      ? PROVIDER_ASSIGNMENT_MESSAGE.sameAsPrimary
      : undefined;

  const handleSubmit = async () => {
    if (error) return;
    await onSubmit({ primaryTherapist, coTherapist });
    onClose();
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      dialogTitle={PROVIDER_ASSIGNMENT_LABEL.title}
      dialogAction={
        <StyledButtonV2
          label={PROVIDER_ASSIGNMENT_LABEL.save}
          variant="outlined"
          disabled={!!error || isSubmitting}
          onClick={handleSubmit}
        />
      }
    >
      <Flex flexDirection="column" gap={2}>
        <ProviderSelect
          label={PROVIDER_ASSIGNMENT_LABEL.primaryTherapist}
          required
          name="primaryTherapist"
          value={primaryTherapist}
          providerSearchParam={PROVIDER_SEARCH_PARAM}
          onChange={setPrimaryTherapist}
        />

        <ProviderSelect
          label={`${PROVIDER_ASSIGNMENT_LABEL.coTherapist} ${PROVIDER_ASSIGNMENT_LABEL.optional}`}
          name="coTherapist"
          value={coTherapist}
          providerSearchParam={PROVIDER_SEARCH_PARAM}
          onChange={setCoTherapist}
          error={isSamePerson && PROVIDER_ASSIGNMENT_MESSAGE.sameAsPrimary}
        />
      </Flex>
    </StyledDialog>
  );
};

export default ProviderAssignmentModal;
